import { useThemeContext } from "./ThemeContext";

export function usePageTheme() {
  const { theme, isLight, isGlassy } = useThemeContext();

  const pick = <T,>(glassy: T, light: T, dark: T): T =>
    isGlassy ? glassy : isLight ? light : dark;

  return {
    isLight,
    isGlassy,
    theme,
    pick,
    // Page backgrounds
    pageBg: pick(
      "bg-gradient-to-br from-[#0b0b1a] via-[#100d24] to-[#0a1228]",
      "bg-gradient-to-br from-rose-50 via-amber-50/80 to-sky-100",
      "bg-gradient-to-b from-[#0a0a0f] via-[#1a0a1f] to-[#0a0a0f]"
    ),
    pageBgFlat: pick(
      "bg-[#0b0b1a]",
      "bg-[#faf8f5]",
      "bg-[#070709]"
    ),
    // Text
    text: pick("text-white", "text-gray-900", "text-white"),
    textSecondary: pick("text-gray-300", "text-gray-600", "text-gray-400"),
    textMuted: pick("text-gray-400", "text-gray-500", "text-gray-500"),
    textSubtle: pick("text-gray-500", "text-gray-400", "text-gray-600"),
    // Cards
    card: pick(
      "bg-white/[0.06] backdrop-blur-xl border border-white/[0.12] shadow-lg shadow-purple-500/5",
      "bg-white/70 backdrop-blur-sm border border-gray-200/60 shadow-sm",
      "bg-white/[0.03] backdrop-blur-sm border border-white/[0.06]"
    ),
    cardHover: pick(
      "hover:bg-white/[0.09] hover:border-white/[0.16]",
      "hover:bg-white/90 hover:border-gray-300/60 hover:shadow-md",
      "hover:bg-white/[0.05] hover:border-white/[0.12]"
    ),
    // Borders
    border: pick("border-white/[0.12]", "border-gray-200/60", "border-white/[0.06]"),
    borderHover: pick("hover:border-white/[0.18]", "hover:border-gray-300", "hover:border-white/[0.12]"),
    // Inputs
    inputBg: pick(
      "bg-white/[0.07] backdrop-blur-lg border border-white/[0.15] text-white placeholder-gray-500",
      "bg-white/80 border border-gray-200/80 text-gray-900 placeholder-gray-400",
      "bg-white/[0.05] border border-white/[0.1] text-white placeholder-white/30"
    ),
    // Buttons
    buttonSecondary: pick(
      "bg-white/[0.06] backdrop-blur-md border border-white/[0.12] text-gray-300 hover:text-white hover:bg-white/[0.1]",
      "bg-white/80 border border-gray-200/80 text-gray-600 hover:bg-gray-100 hover:text-gray-900",
      "bg-white/[0.04] border border-white/[0.08] text-gray-400 hover:bg-white/[0.08] hover:text-white"
    ),
    // Pill / tab inactive
    pillInactive: pick(
      "bg-white/[0.06] border border-white/[0.1] text-gray-400 hover:bg-white/[0.1] hover:text-white",
      "bg-white/60 border border-gray-200/60 text-gray-500 hover:bg-white/90 hover:text-gray-800",
      "bg-white/[0.04] border border-white/[0.06] text-gray-400 hover:bg-white/[0.06] hover:text-white"
    ),
    // Overlay for gradients on light
    sectionOverlay: pick(
      "from-[#0b0b1a]/80 via-[#0b0b1a]/40 to-transparent",
      "from-white/90 via-white/60 to-transparent",
      "from-[#0a0a0f]/80 via-[#0a0a0f]/40 to-transparent"
    ),
    // Whether to show dark theme decorations (star particles, glow orbs)
    showDarkEffects: !isLight,
    // Dividers
    divider: pick("border-white/[0.1]", "border-gray-200/60", "border-white/[0.06]"),
    // Feature icon bg
    iconBg: pick("bg-white/[0.08]", "bg-gray-100", "bg-white/[0.05]"),
  };
}
